import * as React from "react";
import * as RechartsPrimitive from "recharts";

import { cn } from "@/lib/utils";

export type ChartConfig = Record<string, { label?: React.ReactNode; color?: string }>;

const ChartContext = React.createContext<{ config: ChartConfig }>({ config: {} });

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { config: ChartConfig; children: React.ReactElement }
>(({ className, children, config, style, ...props }, ref) => {
  const colorVariables = Object.entries(config).reduce<Record<string, string>>((variables, [key, item]) => {
    if (item.color) variables[`--color-${key}`] = item.color;
    return variables;
  }, {});

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        ref={ref}
        data-chart
        className={cn(
          "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line]:stroke-border/60 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-layer]:outline-none [&_.recharts-surface]:outline-none",
          className
        )}
        style={{ ...colorVariables, ...style } as React.CSSProperties}
        {...props}
      >
        <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = "ChartContainer";

const ChartTooltip = RechartsPrimitive.Tooltip;

type TooltipPayload = { name?: string; value?: number | string; color?: string; dataKey?: string };

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    active?: boolean;
    payload?: TooltipPayload[];
    label?: string;
    labelFormatter?: (value: string) => React.ReactNode;
    indicator?: "line" | "dot";
  }
>(({ active, payload, label, labelFormatter, indicator = "dot", className }, ref) => {
  const { config } = React.useContext(ChartContext);
  if (!active || !payload?.length) return null;

  return (
    <div ref={ref} className={cn("min-w-36 rounded-lg border bg-popover px-3 py-2 text-popover-foreground shadow-md", className)}>
      <p className="mb-2 text-xs font-medium">{labelFormatter && label ? labelFormatter(label) : label}</p>
      <div className="grid gap-1.5">
        {payload.map((item) => {
          const key = String(item.dataKey ?? item.name ?? "");
          return (
            <div key={key} className="flex items-center gap-2 text-xs">
              <span className={indicator === "line" ? "h-0.5 w-3" : "size-2 rounded-full"} style={{ backgroundColor: item.color }} />
              <span className="text-muted-foreground">{config[key]?.label ?? item.name}</span>
              <span className="ml-auto font-medium tabular-nums">{item.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
});
ChartTooltipContent.displayName = "ChartTooltipContent";

export { ChartContainer, ChartTooltip, ChartTooltipContent };
